// Площадка «Авито»: учётные записи, два набора доступов, журнал обмена, вакансии.
// Формы — зеркало contracts/openapi/bark-hiring.v1.yaml.
//
// Главное, что отсюда обязано доехать до экрана: **наборов доступов на записи ДВА, и они
// не взаимозаменяемы** (тз 6.1). Переписка работает только ключом компании, вакансии ведутся
// ключом сотрудника, потому что пакет размещения привязан к сотруднику, а не к компании.
// Поэтому у записи два признака готовности — `can_message` и `can_publish`, — и человек
// видит не «ключ есть», а «что именно этой записью уже можно делать».
//
// Секрет доступа обратно не отдаётся никогда: сервер присылает только `configured`,
// `token_alive` и срок жизни ключа. Поля «показать секрет» на экране быть не может — не
// потому что мы прячем, а потому что его нет в ответе.

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

const BASE = '/api/v1/hiring';

/** contracts/enums/statuses.yaml, avito_key_kind. */
export type KeyKind = 'company' | 'employee';

export interface CredentialOut {
  kind: KeyKind;
  kind_title: string;
  /** Набор доступов заведён. */
  configured: boolean;
  /** Ключ ещё действует. */
  token_alive: boolean;
  /** До какого времени живёт ключ. */
  token_valid_until?: string | null;
  refreshed_at?: string | null;
  refresh_count?: number;
}

export interface AccountOut {
  id: string;
  title: string;
  external_user_id: string | null;
  is_active: boolean;
  note: string | null;
  created_at: string;
  credentials: CredentialOut[];
  /** Переписка возможна: заведён ключ компании. Ключом сотрудника она не работает. */
  can_message: boolean;
  /** Вакансии возможны: заведён хотя бы один набор доступов. */
  can_publish: boolean;
}

export interface TokenOut {
  kind: KeyKind;
  kind_title: string;
  token_valid_until: string;
  refresh_count: number;
}

/** Строка журнала обмена. Персональных данных здесь нет. */
export interface ExchangeOut {
  id: string;
  account_id: string | null;
  kind: KeyKind | null;
  direction: 'out' | 'in';
  operation: string;
  http_status: number | null;
  duration_ms: number | null;
  error_code: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

/** Итог приёма откликов: сколько взяли, сколько карточек завели, сколько повторов. */
export interface PollResultOut {
  fetched: number;
  candidates_created: number;
  repeat_applications: number;
  candidate_ids: string[];
}

export function listAccounts(signal?: AbortSignal): Promise<ListResponse<AccountOut>> {
  return request(`${BASE}/accounts`, { signal });
}

export function createAccount(
  body: { title: string; note?: string | null },
  idempotencyKey: string,
): Promise<AccountOut> {
  return request(`${BASE}/accounts`, { method: 'POST', body, idempotencyKey });
}

export function setCredentials(
  accountId: string,
  kind: KeyKind,
  body: { client_id: string; client_secret: string },
): Promise<AccountOut> {
  return request(`${BASE}/accounts/${accountId}/credentials/${kind}`, { method: 'PUT', body });
}

export function refreshToken(accountId: string, kind: KeyKind): Promise<TokenOut> {
  return request(`${BASE}/accounts/${accountId}/credentials/${kind}/refresh`, { method: 'POST' });
}

export function fetchExchangeLog(
  accountId: string,
  limit: number,
  signal?: AbortSignal,
): Promise<ListResponse<ExchangeOut>> {
  return request(`${BASE}/accounts/${accountId}/exchange-log${queryString({ limit })}`, { signal });
}

export function pollApplications(accountId: string): Promise<PollResultOut> {
  return request(`${BASE}/accounts/${accountId}/applications/poll`, { method: 'POST' });
}

// ── Вакансии ──────────────────────────────────────────────────────────────────────────────

/** contracts/enums/statuses.yaml, vacancy_state. */
export type VacancyState = 'draft' | 'publishing' | 'published' | 'rejected' | 'archived';

export interface VacancyOut {
  id: string;
  account_id: string;
  title: string;
  body: string;
  city: string;
  region: string | null;
  /** Место работы в этом городе: без него площадка не пропускает один текст в двух городах. */
  workplace: string | null;
  key_kind: KeyKind;
  external_id: string | null;
  state: VacancyState;
  state_title: string;
  /** Что ответила площадка на отправку. Приходит раньше решения модерации. */
  processing_status: string | null;
  moderation_status: string | null;
  moderation_status_title: string | null;
  moderation_note: string | null;
  published_at: string | null;
  created_at: string;
}

export interface VacancyIn {
  account_id: string;
  title: string;
  body: string;
  city: string;
  region?: string | null;
  workplace?: string | null;
  site_id?: string | null;
  /** Чьим ключом ведём вакансию. Пакет размещения привязан к сотруднику. */
  key_kind?: KeyKind;
}

/** Сформированный файл выгрузки: где он лежит и сколько объявлений в него попало. */
export interface BulkUploadOut {
  id: string;
  account_id: string;
  state: string;
  feed_url: string;
  vacancy_count: number;
  requested_at?: string | null;
  report?: Record<string, unknown> | null;
}

/** Отчёт площадки о загрузке: по каждому объявлению отдельной строкой (сценарий 106). */
export interface BulkUploadRequestOut {
  id: string;
  state: string;
  requested_at: string;
  accepted: number;
  rejected: number;
  report: Record<string, unknown>;
}

export function listVacancies(
  params: { account_id?: string; state?: string },
  signal?: AbortSignal,
): Promise<ListResponse<VacancyOut>> {
  return request(`${BASE}/vacancies${queryString({ ...params })}`, { signal });
}

export function createVacancy(body: VacancyIn, idempotencyKey: string): Promise<VacancyOut> {
  return request(`${BASE}/vacancies`, { method: 'POST', body, idempotencyKey });
}

export function publishVacancy(vacancyId: string): Promise<VacancyOut> {
  return request(`${BASE}/vacancies/${vacancyId}/publish`, { method: 'POST' });
}

export function refreshVacancyStatus(vacancyId: string): Promise<VacancyOut> {
  return request(`${BASE}/vacancies/${vacancyId}/refresh-status`, { method: 'POST' });
}

/** Что менять и у каких вакансий. Пустое поле означает «не трогать». */
export interface BulkEditIn {
  account_id: string;
  vacancy_ids?: string[] | null;
  city?: string | null;
  title?: string | null;
  body?: string | null;
  workplace?: string | null;
  /** Что заменить в тексте. Работает в паре с replace и вместо целого body. */
  find?: string | null;
  replace?: string | null;
  /** false — только предпросмотр, ничего не меняется. */
  apply: boolean;
}

/** Одна вакансия в предпросмотре: что с ней станет и что этому мешает. */
export interface BulkEditRow {
  id: string;
  title: string;
  city: string;
  state: VacancyState;
  было: string;
  станет: string;
  меняется: boolean;
  /** Почему эту вакансию править нельзя. Пусто — можно. */
  помеха: string | null;
  применено: boolean;
}

export interface BulkEditOut {
  items: BulkEditRow[];
  total: number;
  меняется: number;
  с_помехой: number;
  применено: number;
  применяли: boolean;
}

export function bulkEditVacancies(body: BulkEditIn): Promise<BulkEditOut> {
  return request(`${BASE}/vacancies/bulk-edit`, { method: 'POST', body });
}

export function buildBulkUpload(accountId: string): Promise<BulkUploadOut> {
  return request(`${BASE}/vacancies/bulk-uploads${queryString({ account_id: accountId })}`, {
    method: 'POST',
  });
}

export function requestBulkUpload(uploadId: string): Promise<BulkUploadRequestOut> {
  return request(`${BASE}/vacancies/bulk-uploads/${uploadId}/request`, { method: 'POST' });
}
